import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  Fab,
  List,
  ListItem,
  Notice,
  Skeleton,
  Tabs,
  iconAdd,
  useSnackbar,
  Disclosure,
} from '@sauda/ui/kit';
import { ROLES, type Role } from '@sauda/shared';
import { Screen, useChrome } from '../../app/chrome.js';
import { useWindowClass } from '../../app/media.js';
import { StatePanes } from '../../app/panes.js';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { initials } from '../../lib/initials.js';
import { formatIstDate } from '../../lib/time.js';
import type { Branch, Member, PendingInvite } from '../../lib/types.js';
import { DeactivateWizard } from './DeactivateWizard.js';
import { InviteSheet } from './InviteSheet.js';
import { MemberPanel, MemberSheet } from './MemberSheet.js';

/** The team: members with their role and branch, pending invites, and the invite and removal flows. */
export function TeamScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const agency = auth.agencyId;
  const isOwner = auth.current?.role === 'owner';
  const [tab, setTab] = useState<'members' | 'invites'>('members');
  const [inviting, setInviting] = useState(false);
  const [selected, setSelected] = useState<Member | null>(null);
  const [leaving, setLeaving] = useState<Member | null>(null);
  const [revoking, setRevoking] = useState<PendingInvite | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['members', agency],
    queryFn: () => api<Member[]>('/members'),
  });
  const invites = useQuery({
    queryKey: ['invites', agency],
    queryFn: () => api<PendingInvite[]>('/invites'),
  });
  const branches = useQuery({
    queryKey: ['branches', agency],
    queryFn: () => api<Branch[]>('/branches'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/invites/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRevoking(null);
      await cache.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: () => {
      setRevoking(null);
      setError(t('team.generic'));
    },
  });

  const activeBranches = (branches.data ?? []).filter((b) => b.archivedAt === null);
  const branchName = (id: string) => branches.data?.find((b) => b.id === id)?.name ?? '';
  const defaultBranch = auth.current?.branchId ?? activeBranches[0]?.id ?? '';
  const inviteRoles: Role[] = ROLES.filter((r) => isOwner || r === 'agent');
  const active = (members.data ?? []).filter((m) => m.status === 'active');
  const inactive = (members.data ?? []).filter((m) => m.status !== 'active');
  const canInvite = auth.allowed('members.manage') && !chrome.readOnly;
  const pending = invites.data ?? [];
  const row = (m: Member) => (
    <ListItem
      key={m.id}
      title={m.name ?? m.phone}
      leading={<span className="row-avatar k-t-label-lg">{initials(m.name ?? m.phone)}</span>}
      supporting={`${t(`roles.${m.role}`)} · ${branchName(m.branchId)}`}
      selected={selected?.id === m.id}
      onClick={m.status === 'active' ? () => setSelected(m) : undefined}
      trailing={
        m.status === 'active' ? undefined : (
          <span className="k-t-label">{t('team.deactivated')}</span>
        )
      }
    />
  );
  const windowClass = useWindowClass();
  const twoPane = windowClass === 'expanded' || windowClass === 'large';
  const memberProps = {
    member: selected,
    branches: activeBranches,
    canManage: isOwner && selected?.userId !== auth.userId && !chrome.readOnly,
    onClose: () => setSelected(null),
    onRemove: () => {
      setLeaving(selected);
      setSelected(null);
    },
  };
  const screen = (
    <Screen title={t('team.title')} large eyebrow={auth.current?.agencyName}>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Tabs
        label={t('team.title')}
        value={tab}
        onChange={(key) => setTab(key as 'members' | 'invites')}
        tabs={[
          { key: 'members', label: t('team.members') },
          { key: 'invites', label: t('team.pending'), badge: pending.length },
        ]}
      />
      {members.isPending ? <Skeleton height={72} /> : null}
      {tab === 'members' ? (
        <>
          <p className="k-t-label k-muted">{t('team.seatsNote')}</p>
          <List label={t('team.members')}>{active.map(row)}</List>
          {inactive.length === 0 ? null : (
            <Disclosure summary={t('team.deactivatedGroup', { count: inactive.length })}>
              <List label={t('team.deactivated')}>{inactive.map(row)}</List>
            </Disclosure>
          )}
        </>
      ) : (
        <>
          {pending.length === 0 ? <p className="k-t-body k-muted">{t('team.noPending')}</p> : null}
          <List label={t('team.pending')}>
            {pending.map((i) => (
              <ListItem
                key={i.id}
                title={i.phone}
                supporting={`${t(`roles.${i.role}`)} · ${branchName(i.branchId)} · ${t('team.expires', { date: formatIstDate(i.expiresAt) })}`}
                trailing={
                  chrome.readOnly ? undefined : (
                    <Button className="row-action" variant="tonal" onClick={() => setRevoking(i)}>
                      {t('team.revoke')}
                    </Button>
                  )
                }
              />
            ))}
          </List>
        </>
      )}
      {canInvite ? (
        <div className="fab-slot">
          <Fab icon={iconAdd} label={t('team.invite')} extended onClick={() => setInviting(true)} />
        </div>
      ) : null}
      <InviteSheet
        open={inviting}
        onClose={() => setInviting(false)}
        roles={inviteRoles}
        branches={activeBranches}
        defaultBranchId={defaultBranch}
      />
      {twoPane ? null : <MemberSheet key={selected?.id ?? 'none'} {...memberProps} />}
      <DeactivateWizard
        member={leaving}
        members={members.data ?? []}
        branches={activeBranches}
        onClose={() => setLeaving(null)}
        onDone={async (moved) => {
          setLeaving(null);
          await cache.invalidateQueries({ queryKey: ['members'] });
          snack({ text: t('team.removed', { count: moved }) });
        }}
      />
      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={t('team.revokeTitle')}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setRevoking(null)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              loading={revoke.isPending}
              onClick={() => revoking !== null && revoke.mutate(revoking.id)}
            >
              {t('team.revoke')}
            </Button>
          </>
        }
      >
        {t('team.revokeBody', { phone: revoking?.phone ?? '' })}
      </Dialog>
    </Screen>
  );
  if (!twoPane) return screen;
  return (
    <StatePanes
      list={screen}
      detail={
        selected === null ? null : (
          <Screen title={selected.name ?? selected.phone}>
            <MemberPanel key={selected.id} {...memberProps} />
          </Screen>
        )
      }
      listLabel={t('team.title')}
      detailLabel={t('panes.detail')}
      emptyTitle={t('panes.pickMember')}
    />
  );
}
